import React, { useMemo, useEffect, useState } from 'react';
import { useT } from '../../../i18n';
import { FaChartLine, FaTrophy, FaTimesCircle, FaCoins, FaPercentage, FaSync, FaChartBar } from 'react-icons/fa';
import { SiTether } from 'react-icons/si';
import StatsFilters from './StatsFilters';
import StatsCharts from './StatsCharts';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';

/**
 * Componente para exibir estatísticas do usuário no painel de histórico
 */
const UserStatsPanel = ({ history }) => {
  const t = useT();
  const [refreshKey, setRefreshKey] = useState(0);
  const [showCharts, setShowCharts] = useState(false);
  const [filters, setFilters] = useState({
    timeRange: 'all',
    position: 'all',
    outcome: 'all'
  });
  const [isOpen, setIsOpen] = useState(false); // Para o dropdown mobile

  // Escutar eventos de atualização de histórico
  useEffect(() => {
    const handleHistoryUpdate = () => {
      // Forçar recálculo das estatísticas
      setRefreshKey(prev => prev + 1);
    };
    
    window.addEventListener('historyUpdated', handleHistoryUpdate);
    window.addEventListener('roundClaimed', handleHistoryUpdate);
    window.addEventListener('roundRefunded', handleHistoryUpdate);
    
    return () => {
      window.removeEventListener('historyUpdated', handleHistoryUpdate);
      window.removeEventListener('roundClaimed', handleHistoryUpdate);
      window.removeEventListener('roundRefunded', handleHistoryUpdate);
    };
  }, []);

  // Filtrar histórico com base nos filtros selecionados
  const filteredHistory = useMemo(() => {
    if (!history || history.length === 0) return [];

    return history.filter(entry => {
      // Filtrar por período
      if (filters.timeRange !== 'all') {
        const entryDate = new Date(parseInt(entry.timestamp || Date.now()));
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        const weekStart = new Date(today);
        weekStart.setDate(weekStart.getDate() - weekStart.getDay());
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

        if (filters.timeRange === 'today' && entryDate < today) return false;
        if (filters.timeRange === 'yesterday' && (entryDate < yesterday || entryDate >= today)) return false;
        if (filters.timeRange === 'week' && entryDate < weekStart) return false;
        if (filters.timeRange === 'month' && entryDate < monthStart) return false;
      }

      // Filtrar por posição
      if (filters.position !== 'all' && entry.position !== filters.position) return false;

      // Filtrar por resultado
      if (filters.outcome === 'won' && !entry.won) return false;
      if (filters.outcome === 'lost' && entry.won) return false;

      return true;
    });
  }, [history, filters, refreshKey]);
  
  // Calcular estatísticas com base no histórico filtrado
  const stats = useMemo(() => {
    if (!filteredHistory || filteredHistory.length === 0) {
      return {
        totalBets: 0,
        totalWins: 0,
        totalLosses: 0,
        winRate: 0,
        totalBetAmount: 0,
        totalWinAmount: 0,
        profitLoss: 0,
        profitPercentage: 0
      };
    }

    // Calcular estatísticas básicas
    const totalBets = filteredHistory.length;
    const totalWins = filteredHistory.filter(entry => entry.won).length;
    const totalLosses = totalBets - totalWins;
    const winRate = totalBets > 0 ? (totalWins / totalBets) * 100 : 0;

    // Calcular valores financeiros
    const totalBetAmount = filteredHistory.reduce((sum, entry) => sum + parseFloat(entry.amount), 0);
    const totalWinAmount = filteredHistory.filter(entry => entry.won)
      .reduce((sum, entry) => sum + parseFloat(entry.payout), 0);
    
    // Lucro/Perda = Ganhos totais - Apostas totais
    const profitLoss = totalWinAmount - totalBetAmount;
    
    // Percentual de lucro
    const profitPercentage = totalBetAmount > 0 
      ? (profitLoss / totalBetAmount) * 100 
      : 0;

    return {
      totalBets,
      totalWins,
      totalLosses,
      winRate: winRate.toFixed(1),
      totalBetAmount: totalBetAmount.toFixed(2),
      totalWinAmount: totalWinAmount.toFixed(2),
      profitLoss: profitLoss.toFixed(2),
      profitPercentage: profitPercentage.toFixed(1)
    };
  }, [filteredHistory, refreshKey]);

  // Manipular mudanças nos filtros
  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  return (
    <>
      {/* Mobile: Dropdown compacto */}
      <div className="block md:hidden">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-full bg-[#1a1a1a] rounded-lg p-3 flex justify-between items-center border border-brand/30 hover:border-brand/50 transition-colors"
        >
          <div className="flex items-center gap-2">
            <FaChartLine className="text-brand" size={14} />
            <span className="text-brand font-medium text-sm">{t('bvb.yourStats')}</span>
            <span className="text-xs text-gray-400">
              ({stats.totalBets} bets • {stats.winRate}% win rate)
            </span>
          </div>
          {isOpen ? <FaChevronUp size={12} className="text-gray-400" /> : <FaChevronDown size={12} className="text-gray-400" />}
        </button>

        {isOpen && (
          <div className="mt-2 bg-[#1a1a1a] rounded-lg p-3 border border-brand/30 space-y-3">
            {/* Betting Stats */}
            <div>
              <h4 className="text-xs text-gray-400 mb-2 flex items-center gap-1">
                <FaCoins className="text-yellow-500" size={10} />
                {t('bvb.bettingStats')}
              </h4>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <span className="text-gray-400">{t('bvb.totalBets')}</span>
                  <span className="text-white ml-1 font-medium">{stats.totalBets}</span>
                </div>
                <div>
                  <span className="text-gray-400">{t('bvb.wins')}</span>
                  <span className="text-green-400 ml-1 font-medium">{stats.totalWins}</span>
                </div>
                <div>
                  <span className="text-gray-400">{t('bvb.losses')}</span>
                  <span className="text-red-400 ml-1 font-medium">{stats.totalLosses}</span>
                </div>
                <div>
                  <span className="text-gray-400">{t('bvb.winRate')}</span>
                  <span className="text-blue-400 ml-1 font-medium">{stats.winRate}%</span>
                </div>
              </div>
            </div>
            
            {/* Financial Stats */}
            <div className="pt-2 border-t border-gray-800">
              <h4 className="text-xs text-gray-400 mb-2 flex items-center gap-1">
                <SiTether className="text-[#26A17B]" size={10} />
                {t('bvb.financialStats')}
              </h4>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <span className="text-gray-400">{t('bvb.totalBet')}</span>
                  <span className="text-white ml-1 font-medium">{stats.totalBetAmount} USDT</span>
                </div>
                <div>
                  <span className="text-gray-400">{t('bvb.totalWon')}</span>
                  <span className="text-green-400 ml-1 font-medium">{stats.totalWinAmount} USDT</span>
                </div>
                <div>
                  <span className="text-gray-400">{t('bvb.profitLoss')}</span>
                  <span className={`ml-1 font-medium ${parseFloat(stats.profitLoss) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {parseFloat(stats.profitLoss) >= 0 ? '+' : ''}{stats.profitLoss} USDT
                  </span>
                </div>
                <div>
                  <span className="text-gray-400">{t('bvb.roi')}</span>
                  <span className={`ml-1 font-medium ${parseFloat(stats.profitPercentage) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {parseFloat(stats.profitPercentage) >= 0 ? '+' : ''}{stats.profitPercentage}%
                  </span>
                </div>
              </div>
            </div>

            {/* Charts toggle - Mobile */}
            {filteredHistory.length > 0 && (
              <div className="pt-2 border-t border-gray-800">
                <button 
                  onClick={() => setShowCharts(!showCharts)}
                  className="w-full flex items-center justify-center gap-2 text-xs bg-brand/20 hover:bg-brand/30 text-brand py-2 rounded transition-colors"
                >
                  <FaChartBar size={10} />
                  {showCharts ? t('bvb.hideCharts') : t('bvb.showCharts')}
                </button>

                {/* Charts - Mobile */}
                {showCharts && (
                  <div className="mt-3">
                    <StatsCharts history={filteredHistory} />
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Desktop: Layout normal */}
      <div className="hidden md:block space-y-4">
        
        {/* Painel principal de estatísticas */}
        <div className="bg-[#1a1a1a] rounded-lg p-4 border border-brand/30">
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-lg font-bold flex items-center gap-2">
              <FaChartLine className="text-brand" />
              {t('bvb.yourStats')}
            </h3>
            
            {filteredHistory.length > 0 && (
              <button 
                onClick={() => setShowCharts(!showCharts)}
                className="flex items-center gap-1 text-sm bg-brand/20 hover:bg-brand/30 text-brand px-3 py-1 rounded-full transition-colors"
              >
                <FaChartBar size={12} />
                {showCharts ? t('bvb.hideCharts') : t('bvb.showCharts')}
              </button>
            )}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Estatísticas de apostas */}
            <div className="bg-[#0d0d0d] rounded-lg p-3">
              <h4 className="text-sm text-gray-400 mb-2">{t('bvb.bettingStats')}</h4>
              
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-400 flex items-center gap-1">
                    <FaCoins className="text-yellow-500" size={10} />
                    {t('bvb.totalBets')}
                  </span>
                  <span className="font-medium text-white">{stats.totalBets}</span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-400 flex items-center gap-1">
                    <FaTrophy className="text-green-500" size={10} />
                    {t('bvb.wins')}
                  </span>
                  <span className="font-medium text-green-500">{stats.totalWins}</span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-400 flex items-center gap-1">
                    <FaTimesCircle className="text-red-500" size={10} />
                    {t('bvb.losses')}
                  </span>
                  <span className="font-medium text-red-500">{stats.totalLosses}</span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-400 flex items-center gap-1">
                    <FaPercentage className="text-blue-500" size={10} />
                    {t('bvb.winRate')}
                  </span>
                  <span className="font-medium text-blue-500">{stats.winRate}%</span>
                </div>
              </div>
            </div>
            
            {/* Estatísticas financeiras */}
            <div className="bg-[#0d0d0d] rounded-lg p-3">
              <h4 className="text-sm text-gray-400 mb-2">{t('bvb.financialStats')}</h4>
              
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-400">{t('bvb.totalBet')}</span>
                  <span className="font-medium text-white flex items-center gap-1">
                    <SiTether className="text-[#26A17B]" size={10} />
                    {stats.totalBetAmount}
                  </span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-400">{t('bvb.totalWon')}</span>
                  <span className="font-medium text-green-500 flex items-center gap-1">
                    <SiTether className="text-[#26A17B]" size={10} />
                    {stats.totalWinAmount}
                  </span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-400">{t('bvb.profitLoss')}</span>
                  <span className={`font-medium flex items-center gap-1 ${
                    parseFloat(stats.profitLoss) >= 0 ? 'text-green-500' : 'text-red-500'
                  }`}>
                    <SiTether className="text-[#26A17B]" size={10} />
                    {parseFloat(stats.profitLoss) >= 0 ? '+' : ''}{stats.profitLoss}
                  </span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-400">ROI</span>
                  <span className={`font-medium ${
                    parseFloat(stats.profitPercentage) >= 0 ? 'text-green-500' : 'text-red-500'
                  }`}>
                    {parseFloat(stats.profitPercentage) >= 0 ? '+' : ''}{stats.profitPercentage}%
                  </span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Visualizações gráficas - Desktop */}
          {showCharts && filteredHistory.length > 0 && (
            <div className="mt-4 pt-4 border-t border-brand/20">
              <StatsCharts history={filteredHistory} />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default UserStatsPanel;